import { useCallback, useEffect, useMemo } from 'react'

import { TypedDocumentNode, gql } from '@apollo/client'

import { useQuery, useSubscription } from '@apollo/client/react'

// Helper type to extract the variables type from a query
type InferVariables<T> =
  T extends TypedDocumentNode<unknown, infer V> ? V : never

// Helper type to extract the query result type from a query
type InferQueryResult<T> =
  T extends TypedDocumentNode<infer R, unknown> ? R : never

// Helper type to get the first key of an object type
type FirstKey<T> = T extends object ? keyof T : never

// Helper type to ensure the object has exactly one key
type EnsureSingleKey<T> = T extends object
  ? keyof T extends FirstKey<T>
    ? FirstKey<T>
    : never
  : never

type ExtractItemType<T> = T extends {
  [K in keyof T]: {
    items: Array<infer I>
    metadata: { pageNumber: number; totalPages: number; totalItems: number }
  }
}[keyof T]
  ? I
  : never

// Helper type for the paginated response structure
interface PaginatedResponse<T> {
  metadata: {
    pageNumber: number
    pageSize: number
    totalPages: number
    totalEntries: number
  }
  items: T[]
}

interface SubscriptionResponse<T> {
  created: boolean
  deletedIds: number[]
  updated: T[]
}

const NOOP_SUBSCRIPTION = gql`
  subscription Noop {
    __typename
  }
`

const PAGE_SIZE = 25

export default function usePaginatedQuery<
  TQuery extends TypedDocumentNode,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TSubscription extends TypedDocumentNode<unknown, any>
>(
  query: TQuery,
  variables: Omit<InferVariables<TQuery>, 'page' | 'pageSize'>,
  subscription?: {
    document: TSubscription
    typename: string
    variables?: InferVariables<TSubscription>
  }
) {
  type QueryResult = InferQueryResult<TQuery>
  type QueryName = EnsureSingleKey<QueryResult>
  type ItemType = ExtractItemType<QueryResult>

  const { loading, data, fetchMore, refetch, client } = useQuery<
    QueryResult,
    InferVariables<TQuery>
  >(query, {
    variables: {
      ...variables,
      page: 1,
      pageSize: PAGE_SIZE
    } as InferVariables<TQuery>,
    fetchPolicy: 'cache-and-network',
    refetchWritePolicy: 'overwrite'
  })

  const queryName = useMemo(() => {
    if (!data) return undefined
    return Object.keys(data)[0] as QueryName
  }, [data])

  const loadPage = useCallback(
    (page: number) =>
      fetchMore({
        variables: { page } as unknown as Partial<InferVariables<TQuery>>
      }),
    [fetchMore]
  )

  const refetchAndClear = useCallback(
    (vars?: Partial<InferVariables<TQuery>>) => {
      if (queryName) {
        // Clear the cached paginated data before refetching
        client.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: queryName as string
        })
        client.cache.gc()
      }
      return refetch(vars as InferVariables<TQuery>)
    },
    [client, queryName, refetch]
  )

  useEffect(() => {
    refetchAndClear({ ...variables, page: 1 } as InferVariables<TQuery>)
  }, [variables, refetchAndClear])

  const paginatedResponse = useMemo(() => {
    if (!data || !queryName) return undefined

    return data[queryName] as PaginatedResponse<ItemType>
  }, [queryName, data])

  useSubscription<QueryResult>(subscription?.document ?? NOOP_SUBSCRIPTION, {
    skip: !subscription,
    variables: subscription?.variables,
    onData({ client, data: subData }) {
      if (!subscription || !subData?.data) return

      const subName = Object.keys(subData.data)[0] as QueryName
      const response = subData.data[subName] as SubscriptionResponse<ItemType>

      const hasCreated = response.created
      const deletedIds = response.deletedIds

      // Early return if nothing requires refetch (updates handled by Apollo automatically)
      if (!hasCreated && deletedIds.length === 0) return

      // Handle deletes - evict from cache
      if (deletedIds.length > 0) {
        deletedIds.forEach(id => {
          const identity = client.cache.identify({
            __typename: subscription.typename,
            id
          })

          client.cache.evict({ id: identity })
        })

        client.cache.gc()
      }

      if (paginatedResponse) {
        refetchAndClear({
          ...variables,
          page: paginatedResponse.metadata.pageNumber
        } as InferVariables<TQuery>)
      } else {
        refetchAndClear()
      }
    }
  })

  const items = useMemo(() => {
    if (!data || !paginatedResponse) return undefined

    const index = paginatedResponse.metadata.pageNumber - 1
    const start = index * PAGE_SIZE
    const end = start + PAGE_SIZE

    const items = paginatedResponse.items?.slice(start, end)

    return items
  }, [data, paginatedResponse])

  return {
    items,
    page: queryName
      ? (data?.[queryName] as PaginatedResponse<unknown>)?.metadata.pageNumber
      : undefined,
    metadata: queryName
      ? (data?.[queryName] as PaginatedResponse<unknown>)?.metadata
      : undefined,
    loading,
    loadPage
  }
}
